import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Copy,
    Check,
    CaretDown,
    CaretUp,
    PencilSimple,
    ArrowSquareOut,
    ArrowCounterClockwise,
    Plus,
    Minus,
    X,
    SpinnerGap,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import useAnnouncer from '@/hooks/useAnnouncer';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SEMANTIC_COLORS, DESIGN_INTENTS, VERDICT_PILL, DS_COLORS, DS_SPACING, DS_BORDERS } from '@/config/ui-invariants';
import { AGENT_CONFIG, INTENT_TO_BUTTON, linkedInSignalPill, SHARED_UI_LABELS } from '@/config/task-display-config';
import { regenerateLinkedInPost, saveLinkedInPostEdits, getLinkedInPostRejectReasons, decideIdeaCard, reEvaluateWorkItem } from '@/lib/api';
import IdeaCardRejectMenu, { fetchIdeaCardRejectTaxonomyOnce } from '@/components/shared/IdeaCardRejectMenu';
import GateAnnotationNotice from '@/components/post-studio/GateAnnotationNotice';
import UnresolvedNotes from '@/components/post-studio/UnresolvedNotes';
// WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 (T4) — the closest existing frontend equivalent
// of the backend's _emit_card_render_degraded (work_inbox.py ~L409): a loud, non-silent,
// testable signal already used across this codebase for exposing a divergence between
// what a surface should show and what it actually can. Reused here rather than inventing
// a new reporting path.
import { TRUTH_MARKER } from '@/utils/truthMarker';
import { toast } from 'sonner';

/**
 * LinkedInPostDetail — Detail renderer for linkedin_post work items.
 * Authority: docs/agents/linkedin_post/LINKEDIN_POST_AGENT_SPEC.md Section 8
 *
 * Layout (top to bottom):
 *   Post Type Badge + Signal Strength
 *   Post Text (editable on click) + COPY button
 *   Regeneration Controls (draft only) — Phase 3
 *   Original Draft toggle (after regeneration)
 *   First Comment Text (if blog link exists) + COPY button
 *   Blog Link (clickable)
 *   Source Article (read-only)
 *   Connected Master Doc (read-only, if Type 1)
 *   Associate Briefs (collapsible accordion)
 *   Actions: Approve / Reject (with reason dropdown)
 */

const POST_TYPE_LABELS = {
    thesis_confirmed: 'Thesis Confirmed',
    thesis_challenged: 'Thesis Challenged',
    standalone_insight: 'Standalone Insight',
    emerging_pattern: 'Emerging Pattern',
    contrarian_read: 'Contrarian Read',
};

const ASSOCIATE_LABELS = {
    thesis_validator: 'Thesis Validator',
    contrarian: 'Contrarian',
    pattern_spotter: 'Pattern Spotter',
};

const PS_CALIBRATION = AGENT_CONFIG.post_studio?.calibrationOptions || [];

// Regeneration config from task-display-config.js
const REGEN_CONFIG = AGENT_CONFIG.post_studio?.regeneration || {};
const REGEN_MODIFIERS = REGEN_CONFIG.modifiers || [];
const REGEN_STATES = REGEN_CONFIG.states || { plus: 'plus', minus: 'minus', neutral: 'neutral' };

function CopyButton({ text, label }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success(`${label} copied`);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Could not copy — try selecting manually');
        }
    }, [text, label]);

    return (
        <button
            onClick={handleCopy}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${INTENT_TO_BUTTON.neutral.bg} text-sm font-semibold ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} transition-colors`}
            style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
            aria-label={`Copy ${label}`}
        >
            {copied ? (
                <><Check weight="bold" className="w-4 h-4 text-emerald-600" /> Copied</>
            ) : (
                <><Copy weight="bold" className="w-4 h-4" /> {label}</>
            )}
        </button>
    );
}

function SignalPill({ strength }) {
    // Map signal_strength string or number to a pill config
    let mapping;
    if (typeof strength === 'string') {
        mapping = linkedInSignalPill[strength];
    } else if (typeof strength === 'number') {
        mapping = strength >= 0.8
            ? linkedInSignalPill.strong_signal
            : strength >= 0.5
                ? linkedInSignalPill.signal
                : linkedInSignalPill.noise;
    }
    if (!mapping) {
        mapping = linkedInSignalPill.signal; // fallback
    }

    const pill = VERDICT_PILL[mapping.verdict] || VERDICT_PILL.moderate;
    const shape = VERDICT_PILL.pill;

    return (
        <span
            className="inline-flex items-center gap-1.5"
            style={{
                background: pill.background,
                color: pill.text,
                border: `${shape.borderWidth} solid ${pill.border}`,
                borderRadius: shape.borderRadius,
                fontSize: shape.fontSize,
                fontWeight: shape.fontWeight,
                paddingTop: shape.paddingTop,
                paddingBottom: shape.paddingBottom,
                paddingLeft: shape.paddingLeft,
                paddingRight: shape.paddingRight,
            }}
            aria-label={`Signal: ${mapping.label}`}
        >
            <span
                style={{
                    width: pill.dotSize,
                    height: pill.dotSize,
                    borderRadius: '50%',
                    background: pill.dot,
                    display: 'inline-block',
                }}
            />
            {mapping.label}
        </span>
    );
}

/**
 * ModifierDial — A single plus/minus/neutral toggle for one regeneration modifier.
 * Accessibility: 44px tap targets, bold 16px+ labels, strong contrast.
 * Styles from DESIGN_INTENTS — zero hardcoded hex.
 */
function ModifierDial({ label, state, disabled, onChange }) {
    const isPlus = state === REGEN_STATES.plus;
    const isMinus = state === REGEN_STATES.minus;

    return (
        <div className={`flex items-center justify-between py-2 ${disabled ? 'opacity-40' : ''}`}>
            <span className="text-base font-bold text-slate-800">{label}</span>
            <div className="flex items-center gap-1">
                {/* Minus button */}
                <button
                    onClick={() => onChange(isMinus ? REGEN_STATES.neutral : REGEN_STATES.minus)}
                    disabled={disabled}
                    className={[
                        'w-[44px] h-[44px] flex items-center justify-center rounded-lg border-2 transition-colors font-bold text-lg',
                        isMinus
                            ? `${DESIGN_INTENTS.negative.bg} ${DESIGN_INTENTS.negative.text} ${DESIGN_INTENTS.negative.border}`
                            : 'bg-white text-slate-400 border-slate-200 hover:border-brand-900 hover:text-brand-900',
                        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    ].join(' ')}
                    aria-label={`Decrease ${label}`}
                    aria-pressed={isMinus}
                >
                    <Minus weight="bold" className="w-5 h-5" />
                </button>

                {/* Neutral indicator */}
                <div className={`w-3 h-3 rounded-full mx-1 ${
                    !isPlus && !isMinus ? 'bg-slate-400' : 'bg-slate-200'
                }`} />

                {/* Plus button */}
                <button
                    onClick={() => onChange(isPlus ? REGEN_STATES.neutral : REGEN_STATES.plus)}
                    disabled={disabled}
                    className={[
                        'w-[44px] h-[44px] flex items-center justify-center rounded-lg border-2 transition-colors font-bold text-lg',
                        isPlus
                            ? `${DESIGN_INTENTS.positive.bg} ${DESIGN_INTENTS.positive.text} ${DESIGN_INTENTS.positive.border}`
                            : 'bg-white text-slate-400 border-slate-200 hover:border-brand-900 hover:text-brand-900',
                        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    ].join(' ')}
                    aria-label={`Increase ${label}`}
                    aria-pressed={isPlus}
                >
                    <Plus weight="bold" className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
}

// WI-LINKEDIN-REJECT-REASONS-AND-LEARNING-LOOP-001: module-level cache so
// switching between inbox items doesn't re-fetch the taxonomy on every mount.
let rejectTaxonomyPromise = null;
function fetchRejectTaxonomyOnce() {
    if (!rejectTaxonomyPromise) {
        rejectTaxonomyPromise = getLinkedInPostRejectReasons().catch((err) => {
            // Allow a later render to retry rather than caching a permanent failure.
            rejectTaxonomyPromise = null;
            throw err;
        });
    }
    return rejectTaxonomyPromise;
}

// WI-IDEA-CARD-AMENDMENTS-COMPLETION-001 Leg 2 — the seven-code pre-draft idea-card
// reject vocabulary, fetched once. SEPARATE from the draft-stage taxonomy above: a
// pre-draft card is turned down for a different set of reasons than a finished post,
// and each code carries the two-line definition its button renders (IDEA_CARD_CONTRACT
// §4). One home on the backend (linkedin_post_rules.json::idea_card_reject_taxonomy);
// the card fetches it rather than hardcoding a second copy.
// MOVED (WI-IDEA-LANE-EXECUTE-CEO-RULINGS-001 item B, CEO ruling 2026-07-30) to
// `@/components/shared/IdeaCardRejectMenu`, which is now the single home for BOTH the fetcher
// and the menu. It lived here, and only here, which is why the Post Studio Ideas tab could not
// reuse it and reached for the draft-stage menu instead — offering codes the backend refuses.
// This file now imports it rather than keeping a second copy.

// ── Pre-draft Idea Card — WI-IDEA-CARD-BUILD-001 Leg 2 ─────────────────────
// The idea card IS the existing linkedin_posts TRIGGERED record (Amendment 1,
// docs/contracts/IDEA_CARD_CONTRACT.md §0) — no new object, no new collection.
// This renders BEFORE any drafting spend: the idea, why it clears the gate,
// why-now, and a near-duplicate flag, with three CEO decisions (approve /
// reject / promote). Screen-reader operable per the #950 announcer pattern
// (frontend/src/pages/agents/LinkedInPostAgent.jsx:1645-1852, READ ONLY —
// pattern copied here, that file itself is untouched).

const CARD_LABELS = AGENT_CONFIG.post_studio?.labels || {};
// WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 — the closed verb-token -> label map. A raw
// token (e.g. "re_evaluate") never reaches the screen; every button reads its label
// from here, never a literal string inline.
const VERB_LABELS = CARD_LABELS.verbButtonLabels || {};
const OUTCOME_LABELS = CARD_LABELS.reEvaluationOutcomeLabels || {};

/**
 * hasVerb — whether a token is legal on this card, PER THE SERVER.
 *
 * When `verbs` is not an array at all (every record written before this contract
 * landed), every token is treated as legal — the pre-existing hardcoded action set,
 * unchanged. Once the server sends a real (possibly empty) array, this component
 * renders exactly what it says and nothing else — see verbsAreKnownAndEmpty below for
 * the loud-failure case.
 */
function hasVerb(verbs, token) {
    if (!Array.isArray(verbs)) return true;
    return verbs.includes(token);
}

function verbsAreKnownAndEmpty(verbs) {
    return Array.isArray(verbs) && verbs.length === 0;
}

/**
 * CardActionFailure — the enforceable guard (WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 T4).
 *
 * A card that reaches the CEO with NO legal verb at all is a RENDER FAILURE, not a
 * silently inert card. This renders LOUDLY (role="alert", visible, plain business
 * language — never a raw field name) and reports itself through TRUTH_MARKER, the
 * existing frontend signal for exactly this class of divergence — reused rather than
 * inventing a second reporting path.
 */
function CardActionFailure({ cardId }) {
    useEffect(() => {
        TRUTH_MARKER('CARD_RENDER_DEGRADED', 'INBOX', 'FAIL');
    }, [cardId]);

    return (
        <div
            role="alert"
            aria-live="assertive"
            data-testid="card-verb-render-failure"
            className={`rounded-xl border p-4 ${DESIGN_INTENTS.negative.bg} ${DESIGN_INTENTS.negative.border}`}
        >
            <span className={`text-base font-bold uppercase tracking-wide block mb-1 ${DESIGN_INTENTS.negative.text}`}>
                {CARD_LABELS.noAvailableVerbHeading || 'This card cannot tell us what you can do next'}
            </span>
            <p className={`text-base leading-relaxed ${DESIGN_INTENTS.negative.text}`}>
                {CARD_LABELS.noAvailableVerbBody
                    || 'Something went wrong preparing this card’s actions. We’ve flagged it — please come back to it, or refresh.'}
            </p>
        </div>
    );
}

/** One "why it clears the gate" line — true/false/unknown, never fabricated.
 * WI-IDEA-CARD-ASSEMBLY-REDESIGN-001 (§2j): now carries an optional one-line
 * REASON beside the verdict — `bar_reason_non_obvious` / `bar_reason_grounded`
 * are newly composed; the falsifiable reason reuses `falsification_condition`
 * (§2j: "not a new field... it IS that reason line"). A reason renders under
 * the verdict whenever the backend sent one; its absence is never inferred. */
function IdeaGateLine({ label, value, reason }) {
    const text = value === true ? 'Yes' : value === false ? 'No' : 'Not evaluated';
    const intent = value === true
        ? DESIGN_INTENTS.positive
        : value === false
            ? DESIGN_INTENTS.negative
            : DESIGN_INTENTS.muted;
    return (
        <div className="py-1.5">
            <div className="flex items-center justify-between">
                <span className="text-base text-slate-700">{label}</span>
                <span className={`inline-flex items-center gap-1.5 text-base font-bold ${intent.text}`}>
                    {value === true && <Check weight="bold" className="w-4 h-4" />}
                    {text}
                </span>
            </div>
            {reason ? (
                <p className="text-base text-slate-600 mt-0.5">{reason}</p>
            ) : value === false ? (
                /* A4 (CEO 2026-07-19): "never a bare test under a No". A failed check with no
                   stored reason used to render as the word "No" and nothing else, which reads
                   as the card refusing to explain itself. A passing check needs no such line —
                   absence of prose under a Yes is not a contradiction. */
                <p className="text-base italic text-slate-500 mt-0.5">
                    {CARD_LABELS.barFailureNoReason || 'no reason was recorded for this one'}
                </p>
            ) : null}
        </div>
    );
}

// WI-IDEA-CARD-ASSEMBLY-REDESIGN-001 (§2j row 5) — the receipt line: per-category
// read counts, rendered from what the shelf reader honestly attests it consumed
// (backend/core/agents/linkedin_post/card_shelf_reader.py). Never a raw category
// key on screen — CATEGORY_LABELS (task-display-config.js) names every live and
// deferred category; an unknown future category still renders (title-cased),
// so a new shelf category never silently vanishes from the receipt.
// §2m (CEO 2026-07-20) — the case's labelled slots, in render order. Each is filled ONLY
// from its own named source component of the triggering article; cross-filling is refused at
// composition time. The labels are what makes the sourcing legible to the reader: the founding
// evidence was a card whose one blended paragraph made 5,500 characters of separately-earned
// analysis indistinguishable.
const CASE_SLOT_ORDER = [
    { key: 'pattern_context', labelKey: 'caseSlotPatternContext' },
    { key: 'consequence', labelKey: 'caseSlotConsequence' },
    { key: 'counter_argument', labelKey: 'caseSlotCounterArgument' },
];

const CATEGORY_LABELS = AGENT_CONFIG.post_studio?.labels?.receiptCategoryLabels || {};
const CATEGORY_LABELS_PLURAL = AGENT_CONFIG.post_studio?.labels?.receiptCategoryLabelsPlural || {};

function _categoryLabel(category) {
    return CATEGORY_LABELS[category] || String(category || '').replace(/_/g, ' ');
}

/** The plural of a category label, DECLARED not constructed.
 *
 * The naive `label + 's'` this replaces rendered "5 KB entrys" on the CEO's card —
 * English -y/-ies plurals cannot be suffixed, and a receipt that misspells its own
 * subject undercuts the thing it exists to prove. Declared plurals win; the +'s'
 * fallback covers only regular labels that have no entry.
 */
function _categoryLabelPlural(category) {
    const declared = CATEGORY_LABELS_PLURAL[category];
    if (declared) return declared;
    return `${_categoryLabel(category)}s`;
}

// WI-TC-POST-STUDIO-LANE-RULES-SELECTION-AND-CONSEQUENCE-2026-10-03 (F2, ruling 257) — where each
// part of the card came from. The backend (selection_sources.py) records, for every case slot,
// the falsifier and why-it-won, the material it was taken from and why; this only words it. A card
// that predates the record says so rather than showing nothing, and the words are the backend's
// own measured counts, never a restated rule.
const SELECTION_FIELD_WORDS = AGENT_CONFIG.post_studio?.labels?.selectionFieldWords || {};

function _selectionFieldWord(field) {
    return SELECTION_FIELD_WORDS[field] || String(field || '').replace(/_/g, ' ');
}

export function buildSlotSourceLine(selectionSources, slotKey) {
    const entry = selectionSources?.slots?.[slotKey];
    if (!entry || typeof entry !== 'object') return '';
    const field = _selectionFieldWord(entry.source?.field);
    if (entry.state === 'honest_empty') {
        return `empty: the article carried no ${field}, so nothing was borrowed from elsewhere`;
    }
    const trace = entry.trace || {};
    const measured = Number.isFinite(trace.found_in_source) && Number.isFinite(trace.content_words) && trace.content_words > 0
        ? `; ${trace.found_in_source} of its ${trace.content_words} key words are found there`
        : '';
    return `filed under the article's ${field}, the only source this part may use${measured}`;
}

// R04 (CEO 20 Oct 2026): the case is ONE composed view and each claim in it carries the passage that
// backs it. The backend records the claims in selection_sources.slots.case.claims (version 2 joins);
// this only words them. A claim no passage backs is said to be not yet backed, never dropped silently.
export function buildCaseClaimLines(selectionSources) {
    const claims = selectionSources?.slots?.case?.claims;
    if (!Array.isArray(claims)) return [];
    return claims
        .filter((c) => c && typeof c.claim === 'string' && c.claim.trim())
        .map((c) => {
            const backed = c.state === 'grounded' && c.source;
            return {
                claim: c.claim.trim(),
                stance: c.stance === 'against' || c.stance === 'holds' ? c.stance : '',
                backed: Boolean(backed),
                source: backed ? String(c.source) : '',
                passage: backed && c.passage ? String(c.passage).trim() : '',
            };
        });
}

export function buildSelectionFooter(selectionSources, status) {
    if (status === 'not_recorded_card_predates') {
        return (AGENT_CONFIG.post_studio?.labels?.selectionNotRecorded
            || 'Where each part came from was not recorded: this card was built before cards kept that record.');
    }
    const slots = selectionSources?.slots;
    if (!slots) return '';
    const clauses = [];
    if (status === 'derived_from_stored_card') {
        // A card built before cards kept this record: the join is rebuilt from what is stored on
        // it, and the screen says so rather than presenting it as a record made at the time.
        clauses.push(AGENT_CONFIG.post_studio?.labels?.selectionRebuilt
            || 'rebuilt from this card\'s stored analysis, not recorded when it was built');
    }
    const falsifier = slots.falsifier;
    if (falsifier) {
        const named = Array.isArray(falsifier.traced_to) ? falsifier.traced_to : [];
        if (named.length) {
            const words = named.map((t) => {
                const where = t.kind === 'article_field' ? `the article's ${_selectionFieldWord(t.field)}` : String(t.category || '').replace(/_/g, ' ');
                return `${where} (${t.shared_words} shared words)`;
            });
            clauses.push(`the test that would prove it wrong was composed from everything read; it shares most wording with ${words.join(', ')}`);
        } else {
            clauses.push('the test that would prove it wrong was composed as a forward test and is traced to no single source');
        }
    }
    if (slots.why_won?.reason) clauses.push(`why it won: ${slots.why_won.reason}`);
    return clauses.join('; ');
}

// WI-TC-POST-STUDIO-LANE-RULES-SELECTION-AND-CONSEQUENCE-2026-10-03 (F1, ruling 257) — the
// firm's lane and timing rules applied to this card, as the backend read them
// (backend/core/agents/linkedin_post/lane_timing_reader.py). The reasons are the backend's own
// plain-words sentences; this function adds only the value word and the join, so the rule
// itself is never restated on the screen. An unwritten rule renders as "unknown" with its
// reason, never as a quiet pass.
const LANE_TIMING_VALUE_LABELS = AGENT_CONFIG.post_studio?.labels?.laneTimingValueLabels || {};

function _laneTimingValueWord(value) {
    return LANE_TIMING_VALUE_LABELS[value] || String(value || '').replace(/_/g, ' ');
}

export function buildLaneTimingLine(laneTiming) {
    if (!laneTiming || typeof laneTiming !== 'object') return '';
    const clauses = [];
    const lane = laneTiming.lane;
    if (lane && lane.value) {
        clauses.push(`lane ${_laneTimingValueWord(lane.value)}${lane.reason ? ` (${lane.reason})` : ''}`);
    }
    const freshness = laneTiming.timing?.freshness;
    if (freshness && freshness.value === 'dated' && freshness.reason) {
        // A date is provenance, never a verdict: say where it came from, in the backend's own words.
        clauses.push(freshness.reason);
    } else if (freshness && freshness.value) {
        clauses.push(`date ${_laneTimingValueWord(freshness.value)}${freshness.reason ? ` (${freshness.reason})` : ''}`);
    }
    const wrongWeek = laneTiming.timing?.wrong_week;
    if (wrongWeek && wrongWeek.value) {
        clauses.push(`wrong-week ${_laneTimingValueWord(wrongWeek.value)}${wrongWeek.reason ? ` (${wrongWeek.reason})` : ''}`);
    }
    return clauses.join('; ');
}

export function buildReceiptLine(receipt, deferredCategories) {
    const clauses = [];
    Object.entries(receipt || {}).forEach(([category, entry]) => {
        if (!entry || typeof entry !== 'object') return;
        const label = _categoryLabel(category);
        if (entry.state === 'read') {
            const count = Number.isFinite(entry.consumed_items) ? entry.consumed_items : 0;
            if (category === 'source_article') {
                clauses.push(`read ${label}`);
            } else {
                // A5: the receipt NAMES what it read. A bare count cannot be checked against
                // anything — the CEO's own objection. `entries` are real titles only (the
                // backend refuses to pass a database id off as a name), so their absence
                // simply falls back to the count rather than printing something unreadable.
                const names = Array.isArray(entry.entries) ? entry.entries.filter(Boolean) : [];
                const noun = count === 1 ? label : _categoryLabelPlural(category);
                let clause = `read ${count} ${noun}`;
                // "3 of the closest 5" — makes a CUT visible as a cut. Without this a top-5
                // slice reads identically to an exhaustive sweep of the whole shelf.
                if (Number.isFinite(entry.total_available)) {
                    clause = `read ${count} of ${entry.total_available} ${noun}`;
                } else if (Number.isFinite(entry.limit_applied)) {
                    clause = `read ${count} of the closest ${entry.limit_applied} ${noun}`;
                }
                if (names.length) {
                    const shown = names.join('; ');
                    const more = count > names.length ? `; and ${count - names.length} more` : '';
                    clause += ` — ${shown}${more}`;
                }
                // D-2026-07-20-13: material reached by the SIMILARITY FALLBACK is labelled
                // for what it is. Nothing here was tagged to this subject — it is the nearest
                // thing we hold and may be about something else entirely. The founding
                // incident was a diaper card whose "closest five" were mobility and
                // IPL-fintech pieces, presented indistinguishably from real precedent.
                if (entry.retrieval_mode === 'nearest_unrelated') {
                    clause += ` (${CARD_LABELS.receiptNearestUnrelated || 'nothing tagged to this subject — nearest material we hold, may be unrelated'})`;
                }
                // R05: what the search was made on is kept on the receipt and said in words.
                if (entry.query_basis) {
                    clause += ` (${(CARD_LABELS.receiptSearchedOnTemplate || 'searched on {basis}').replace('{basis}', entry.query_basis)})`;
                }
                clauses.push(clause);
            }
        } else if (entry.state === 'nothing_exists') {
            clauses.push(`${label}: none found`);
        } else if (entry.state === 'failed') {
            clauses.push(`${label}: failed to read, so this idea was written without it`);
        }
    });
    (deferredCategories || []).forEach((deferred) => {
        const label = _categoryLabel(deferred?.category);
        if (label) clauses.push(`${label}: not yet built`);
    });
    return clauses.join('; ');
}

/** Reason-taxonomy dropdown shared by Reject and Promote — both require a
 * structured reason from GET /api/linkedin-posts/reject-reasons (free text
 * alone is refused, per contract §4/§8). */
function CardReasonMenu({ label, icon, intentClass, taxonomy, disabled, align, onPick }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled}
                    className={`gap-1.5 text-base font-semibold shadow-sm disabled:opacity-60 ${intentClass}`}
                    style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                    aria-label={label}
                >
                    {icon} {label} <CaretDown weight="bold" className="w-3 h-3" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={align} className="z-[100] bg-white border border-slate-200 shadow-lg rounded-md w-72 max-h-96 overflow-y-auto">
                {taxonomy.groups.map((group, idx) => (
                    <React.Fragment key={group.attribution}>
                        {idx > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuLabel className="text-sm font-bold uppercase tracking-wide text-slate-500">
                            {group.group_label}
                        </DropdownMenuLabel>
                        {(group.reasons || []).map((reason) => (
                            <DropdownMenuItem
                                key={reason.id}
                                className="text-base py-3 cursor-pointer font-semibold text-slate-800"
                                onSelect={() => onPick(reason.id, group.attribution)}
                            >
                                {reason.label}
                            </DropdownMenuItem>
                        ))}
                    </React.Fragment>
                ))}
                {taxonomy.other && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            className="text-base py-3 cursor-pointer font-semibold text-slate-800"
                            onSelect={() => onPick(taxonomy.other.id, null)}
                        >
                            {taxonomy.other.label}
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// WI-IDEA-CARD-AMENDMENTS-COMPLETION-001 Leg 2 — the pre-draft card's Reject menu.
// Renders the SEVEN codes, each with the two-line definition the CEO reads before
// choosing (IDEA_CARD_CONTRACT §4). Distinct from CardReasonMenu above: that renders
// the draft-stage groups/other shape; this renders the flat seven-code `codes` shape
// with its definitions. Picking a code fires the reject immediately — one tap, no
// free-text step (free text stays optional at the endpoint, not required here).
// IdeaCardRejectMenu MOVED to `@/components/shared/IdeaCardRejectMenu` — see the note above
// the fetcher. Imported at the top of this file; not redefined here.

// onRefresh refetches the inbox list + summary tiles. Approve/promote call the
// card-decision API directly (they do not route through the cockpit's
// onAccept/onReject), so without this the card announced "Idea approved" while
// the list and the tile counts kept showing the decided item until a manual
// reload — a spoken confirmation over stale data. Reject already refreshes
// because it routes through the onReject prop.

// WI-IDEA-FULL-READ-AND-CONFIRM-IN-FLIGHT-001 Leg 1b — the ratified numbered-order
// BODY, split out of PreDraftIdeaCard so a second surface (the Post Studio Ideas
// tab) can render the same read WITHOUT a second set of decision buttons. This is
// a REUSE SPLIT per docs/findings/IDEA_TAB_FULL_READ_WIRING_MAP_2026-07-31.md — one
// producer of the JSX, two consumers — not a second component: every derived field
// and every honesty fallback below (caseSlotEmpty, barFailureNoReason,
// dateNotCapturedLabel, the receipt's honest-gap clauses, the View-check line) is
// the SAME code that lived inline in PreDraftIdeaCard, unchanged.
//
// `PreDraftIdeaCard` (below) renders this body plus its OWN action bar, for the
// Work Inbox surface — unchanged behaviour there. `IdeaTabPanel` renders this body
// alone, wrapped as `{ payload: idea }` (the ideas route and the Work Inbox route
// now share one payload producer, so `idea` and `payload` carry the same field
// names), under IdeaTabPanel's own pre-existing action bar.
//
// `intendedMessage` falls back to `payload.claim` — the two routes have historically
// named the identical field differently (work_inbox's WorkItem payload carries
// `intended_message`; the ideas route derives a `claim` field FROM intended_message
// for its own pre-existing pill/title use). Both names denote the same sentence, so
// reading either is not a substitute value — it is the same fact under its other name.
// 6 Oct 2026 (CEO): the card's claim is composed AFTER the material is assembled, in the card's one call, or it is the lifted
// sentence as the bounded fallback. The receipt says which, in plain words, so the CEO never has to guess what he is reading.
// The backend (idea_card_builder._judge_composed_claim) owns the state and the named reason; this only passes them through.
const CLAIM_FALLBACK_REASONS = {
    company_level: 'the composed claim was about one company',
    no_probabilistic_register: 'the composed claim read as a verdict, not a reasoned possibility',
    not_enough_material: 'the material did not support three or more of the five composition tests',
    field_label: 'the composed claim was a label, not a claim',
    enumeration_marker: 'the composed claim was a list marker, not a claim',
};
export function buildClaimSourceLine(claimComposition) {
    if (!claimComposition || typeof claimComposition !== 'object') return '';
    if (claimComposition.state === 'composed') {
        const unknown = Number(claimComposition.answers_unknown || 0);
        return `Claim composed from the assembled material${unknown ? ` (${unknown} of 5 tests: not enough material)` : ''}`;
    }
    if (claimComposition.state === 'lifted_fallback') {
        const reason = CLAIM_FALLBACK_REASONS[claimComposition.fallback_reason] || claimComposition.fallback_reason || 'no reason recorded';
        return `Claim is the article analysis's own sentence: the composed claim did not stand (${reason})`;
    }
    return '';
}

export function IdeaCardBody({ item }) {
    const payload = item.payload || {};
    const typeLabel = payload.post_type_label || POST_TYPE_LABELS[payload.post_type] || payload.post_type || '';
    const intendedMessage = payload.intended_message || payload.claim || '';
    const gateReasoning = payload.idea_gate_reasoning || {};
    const ideaCard = payload.idea_card || {};
    const claimSourceLine = buildClaimSourceLine(ideaCard.claim_composition);
    // WI-IDEA-CARD-ASSEMBLY-REDESIGN-001 (§2j) — the render contract's own fields.
    // The falsifiable bar reason reuses falsification_condition (§2j: "not a new
    // field... it IS that reason line"); only the other two are newly composed.
    // §2m (CEO 2026-07-20): the case is LABELLED SLOTS, each from its own named source
    // component of the triggering article. A pre-§2m stored card carries a plain string and
    // still renders — as one unlabelled block, which is exactly what it was.
    const caseSlots = (ideaCard.case && typeof ideaCard.case === 'object') ? ideaCard.case : null;
    const caseText = (typeof ideaCard.case === 'string') ? ideaCard.case : '';
    const barReasonNonObvious = ideaCard.bar_reason_non_obvious || '';
    const barReasonGrounded = ideaCard.bar_reason_grounded || '';
    const barReasonFalsifiable = ideaCard.falsification_condition || '';
    const triggerBlock = ideaCard.trigger_block || null;
    const receipt = ideaCard.receipt || null;
    const deferredCategories = ideaCard.deferred_categories || [];
    const whyNow = ideaCard.why_now || '';
    // Legacy fallback for a card that has not been rebuilt with a trigger_block yet
    // (the required-fields wall withholds these going forward, but the fields stay
    // read defensively rather than rendering a blank section on a stale record).
    const sourceTitle = triggerBlock?.title || payload.source_article_title || '';
    const sourceUrl = triggerBlock?.link || payload.source_article_url || '';
    const nearDuplicate = payload.near_duplicate || null;
    // §2e fallback — the card was produced without a ratified View backing it.
    // DEMOTED (§2j row 6) from a banner to a quiet line — see the render below.
    const noViewFallback = Boolean(payload.no_view_fallback);
    const noViewStamp = payload.no_view_stamp || '';
    const fallbackProof = payload.fallback_proof || '';
    // WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 (T3) — a withheld idea card carries
    // gate_annotations and available_verbs in the SAME shape the draft branch already
    // renders (WI-WITHHELD-CARD-ROUTE-BACK-001 landed the write same day). cardItemId is
    // the id the repair/overrule endpoints key on — post_id when present (the stable
    // linkedin_posts._id), falling back to the WorkItem id for a shape that carries none.
    const cardItemId = payload.post_id || item.id;
    const gateAnnotations = payload.gate_annotations || [];
    const availableVerbs = payload.available_verbs;

    // WI-IDEA-CARD-ASSEMBLY-REDESIGN-001 (§2j): "A No on any bar check never
    // passes silently. It renders flagged loudly at the top of the bar." This is a
    // distinct announcer from the action-outcome one PreDraftIdeaCard owns —
    // this one speaks on arrival, not in response to a CEO action — and reuses
    // the SAME useAnnouncer dedup mechanism so a refreshed item carrying the
    // identical failure is re-spoken, never silently swallowed as a React no-op.
    const [barFlagMessage, announceBarFlag] = useAnnouncer();
    // F3 (ruling 257): the consequence test is the bar's fourth check, shown ADVISORY: it appears as
    // a line on the bar but is deliberately NOT part of the loud "Did not clear the bar" banner, because
    // the 3 Oct replay showed it fails most accepted cards (22 of 33) and it has not been agreed with
    // the CEO (red team RT-TC-F1F3 item 3). The banner stays what it was: three checks. The backend (idea_card_bar.py)
    // evaluates it and sends the verdict with its plain-words reason, so the screen never restates the
    // rule. `passes: null` means the test could not run, which renders as "Not evaluated" and never
    // counts as a failure.
    const consequenceCheck = (Array.isArray(payload.bar?.checks)
        ? payload.bar.checks.find((check) => check.key === 'consequence')
        : null) || null;
    const consequenceValue = consequenceCheck ? consequenceCheck.passes : null;
    const consequenceReason = consequenceCheck?.reason || '';
    const failedBarChecks = useMemo(() => {
        const checks = [
            { value: gateReasoning.non_obvious, label: CARD_LABELS.ideaGateNonObvious, reason: barReasonNonObvious },
            { value: gateReasoning.groundable_anchor, label: CARD_LABELS.ideaGateGrounded, reason: barReasonGrounded },
            { value: gateReasoning.falsifiable, label: CARD_LABELS.ideaGateFalsifiable, reason: barReasonFalsifiable },
        ];
        return checks.filter((check) => check.value === false);
    }, [gateReasoning.non_obvious, gateReasoning.groundable_anchor, gateReasoning.falsifiable,
        barReasonNonObvious, barReasonGrounded, barReasonFalsifiable]);

    // A4: ONE detail string, used by the spoken announcement AND the visible banner's
    // aria-label. They drifted before — the announcer carried the reason and the banner did
    // not — so a sighted reader saw less than a screen-reader user was told. Sharing the
    // source makes that particular divergence unrepresentable.
    const barFlagDetail = useMemo(() => failedBarChecks
        .map((check) => `${check.label} — ${check.reason || (CARD_LABELS.barFailureNoReason || 'no reason was recorded for this one')}`)
        .join('. '), [failedBarChecks]);

    useEffect(() => {
        if (failedBarChecks.length === 0) return;
        announceBarFlag(`${CARD_LABELS.barFailureFlagPrefix || 'Did not clear the bar:'} ${barFlagDetail}`);
        // `item` (not just postId) is the dependency: a refreshed record with an
        // unchanged failing verdict must still re-announce — the exact repeat
        // case useAnnouncer exists to handle (see its module docstring).
    }, [item, failedBarChecks, barFlagDetail, announceBarFlag]);

    // A6 — the View check, in plain words. Three distinct outcomes, never blurred:
    // a View was found (say which, and on what); the search ran and nothing carries those
    // tags (say which tags); or the article has no tags to search at all (say that, rather
    // than implying a search happened and came back empty).
    const viewLookupLine = useMemo(() => {
        const lookup = ideaCard.view_lookup;
        if (!lookup || typeof lookup !== 'object') return '';
        const searched = lookup.tags_searched || {};
        const sectorTags = [...(searched.l3 || []), ...(searched.l2 || []), ...(searched.l1 || [])]
            .filter(Boolean);
        if (!sectorTags.length) {
            return 'this article carries no sector tags, so no View could be looked up';
        }
        const tagList = sectorTags.join(', ');
        if (lookup.view_key) {
            const on = lookup.matched_on?.tag;
            const overlap = Array.isArray(lookup.signal_overlap) ? lookup.signal_overlap : [];
            return [
                `View found${on ? ` under ${on}` : ''}`,
                `searched ${tagList}`,
                overlap.length ? `sharing ${overlap.join(', ')}` : '',
            ].filter(Boolean).join('; ');
        }
        return `no ratified View carries these tags — searched ${tagList}`;
    }, [ideaCard.view_lookup]);

    const receiptLine = useMemo(
        () => buildReceiptLine(receipt, deferredCategories),
        [receipt, deferredCategories]
    );

    const laneTimingLine = useMemo(
        () => buildLaneTimingLine(payload.lane_timing),
        [payload.lane_timing]
    );

    const slotSourceLines = useMemo(
        () => Object.fromEntries(
            CASE_SLOT_ORDER.map(({ key }) => [key, buildSlotSourceLine(payload.selection_sources, key)])
        ),
        [payload.selection_sources]
    );
    const caseClaimLines = useMemo(
        () => buildCaseClaimLines(payload.selection_sources),
        [payload.selection_sources]
    );
    const selectionFooter = useMemo(
        () => buildSelectionFooter(payload.selection_sources, payload.selection_sources_status),
        [payload.selection_sources, payload.selection_sources_status]
    );

    return (
        <>
            {/* WI-IDEA-CARD-ASSEMBLY-REDESIGN-001 (§2j): the bar-failure flag's own
                announcer — role="alert" (not "status", to stay a DISTINCT,
                unambiguous region from the polite action-status region the action-bar
                owner renders, each independently queryable) with its native implicit
                aria-live="assertive": a card opening with an unflagged failing
                bar check is the founding incident this leg exists to fix. */}
            <div role="alert" aria-live="assertive" className="sr-only">{barFlagMessage}</div>

            <div className="flex items-center gap-3 flex-wrap">
                <span className={`inline-flex px-3 py-1.5 rounded-full text-base font-bold border ${SEMANTIC_COLORS.confidence.high.bg} ${SEMANTIC_COLORS.confidence.high.text} ${SEMANTIC_COLORS.confidence.high.border}`}>
                    Content Idea — {typeLabel}
                </span>
            </div>

            {/* WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 (T3) — the objecting rule and its
                quoted sentence, on the idea-card branch. Mounted here, ABOVE the case/claim
                body below, for the same reason it sits above the draft body on the other
                branch (LinkedInPostDetail's main render, and FailureReceiptCard): it quotes
                a sentence FROM that body. The existing component, the existing field, the
                existing shape — no new component, no new vocabulary. */}
            <GateAnnotationNotice
                annotations={gateAnnotations}
                itemId={cardItemId}
                availableVerbs={Array.isArray(availableVerbs) ? availableVerbs : []}
            />

            {/* §2j row 1 — Claim */}
            {intendedMessage && (
                <div
                    className={`rounded-xl border p-4 ${DESIGN_INTENTS.info.bg} ${DESIGN_INTENTS.info.border}`}
                    aria-label={`The idea: ${intendedMessage}`}
                >
                    <span className={`text-base font-bold uppercase tracking-wide block mb-1 ${DESIGN_INTENTS.info.text}`}>
                        The idea
                    </span>
                    <p className={`text-lg leading-relaxed ${DESIGN_INTENTS.info.text}`}>{intendedMessage}</p>
                    {claimSourceLine && (
                        <p className={`text-base mt-2 ${DESIGN_INTENTS.info.text}`} data-testid="claim-source-line">{claimSourceLine}</p>
                    )}
                </div>
            )}

            {/* R08 — how the idea stands against our earlier posts, in one line; the CEO decides, nothing stops the card for it. */}
            {ideaCard.prior_post_relation && ideaCard.prior_post_relation.line && (
                <p className="text-base text-slate-700" data-testid="prior-post-relation">
                    <span className="font-bold">{CARD_LABELS.relationHeading || 'Against our earlier posts'}:</span>{' '}
                    {ideaCard.prior_post_relation.line}
                </p>
            )}

            {/* §2j row 2 — Case: what the post would argue, its strongest evidence,
                and the counter-point it survives. NEVER the same text as the Claim
                above — the composer refuses an echoing case before it ever reaches
                here (backend/core/agents/linkedin_post/idea_card_builder.py). */}
            {(caseSlots || caseText) && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <span className="text-base font-bold uppercase tracking-wide block mb-1 text-slate-500">
                        {CARD_LABELS.caseHeading || 'The case'}
                    </span>
                    {caseSlots ? (
                        <div className="space-y-3">
                            {CASE_SLOT_ORDER.map(({ key, labelKey }) => {
                                const text = (caseSlots[key] || '').trim();
                                return (
                                    <div key={key}>
                                        <span className="text-base font-bold text-slate-600 block">
                                            {CARD_LABELS[labelKey]}
                                        </span>
                                        {text ? (
                                            <p className="text-base leading-relaxed text-slate-700">{text}</p>
                                        ) : (
                                            /* An empty slot says so. §2m: its source component was
                                               absent, and an honest gap beats a slot quietly filled
                                               from somewhere it was not allowed to come from. */
                                            <p className="text-base italic text-slate-500">
                                                {CARD_LABELS.caseSlotEmpty || 'the article carried nothing for this'}
                                            </p>
                                        )}
                                        {slotSourceLines[key] && (
                                            <p className="text-base text-slate-500 mt-0.5">{slotSourceLines[key]}</p>
                                        )}
                                        {/* Provenance renders ONLY when it varies from ours — the
                                            resolution that made the carded A2 free instead of a
                                            constant printed on every card forever (§2m). */}
                                        {key === 'counter_argument'
                                            && text
                                            && caseSlots.counter_provenance
                                            && caseSlots.counter_provenance !== 'ours' && (
                                            <p className="text-base text-slate-500 mt-0.5">
                                                {CARD_LABELS.caseCounterFromArticle || "this is the article's own counter-argument, not ours"}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-base leading-relaxed text-slate-700">{caseText}</p>
                    )}
                    {!caseSlots && caseClaimLines.length > 0 && (
                        <div className="mt-3 space-y-3" role="group" aria-label={CARD_LABELS.caseClaimsHeading || 'Where each point comes from'}>
                            {/* A card whose claims carry no stance (born before 21 Oct 2026) keeps one flat list: it cannot be
                                told what argues for and what against, and must not claim nothing argues against. */}
                            {(caseClaimLines.some((line) => line.stance)
                                ? [
                                { stance: 'holds', heading: CARD_LABELS.caseHoldsHeading || 'Why it holds', none: '' },
                                { stance: 'against', heading: CARD_LABELS.caseAgainstHeading || 'The strongest case against it',
                                  none: CARD_LABELS.caseAgainstNone || 'No case against it was set out on this card.' },
                                ]
                                : [{ stance: '' }]
                            ).map(({ stance, heading, none }) => {
                                const group = caseClaimLines.filter((line) => line.stance === stance);
                                if (!group.length && !none) return null;
                                return (
                                    <div key={stance}>
                                        {heading && <span className="text-base font-bold text-slate-600 block">{heading}</span>}
                                        {group.length === 0 ? (
                                            <p className="text-base italic text-slate-500">{none}</p>
                                        ) : (
                                            <ul className="space-y-2">
                                                {group.map((line, index) => (
                                                    <li key={`${index}-${line.claim}`} className="text-base text-slate-600">
                                                        {line.backed ? (
                                                            <>
                                                                <span className="font-bold">{line.source}:</span>{' '}
                                                                {line.passage ? `\u201C${line.passage}\u201D` : line.claim}
                                                            </>
                                                        ) : (
                                                            <span className="italic">
                                                                {(CARD_LABELS.caseClaimNotBacked || 'Not yet backed by a passage in our material')}: {line.claim}
                                                            </span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {selectionFooter && (
                        <p className="text-base text-slate-500 mt-3" role="note" aria-label={selectionFooter}>
                            <span className="font-bold">{CARD_LABELS.selectionHeading || 'Where this came from'}:</span> {selectionFooter}
                        </p>
                    )}
                </div>
            )}

            {/* R07 — the one takeaway, with the result of the field-swap test stated in plain words when it is generic. */}
            {ideaCard.what_to_convey && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <span className="text-base font-bold uppercase tracking-wide block mb-1 text-slate-500">
                        {CARD_LABELS.takeawayHeading || 'The takeaway'}
                    </span>
                    <p className="text-base leading-relaxed text-slate-700">{ideaCard.what_to_convey}</p>
                    {ideaCard.takeaway_swap_test === 'generic' && (
                        <p className="text-base text-slate-500 mt-1">
                            {CARD_LABELS.takeawayGeneric || 'This takeaway could be said of any field, not only this one.'}
                        </p>
                    )}
                    {ideaCard.takeaway_swap_test === 'not_answered' && (
                        <p className="text-base text-slate-500 mt-1">
                            {CARD_LABELS.takeawayNotChecked || 'This takeaway was not checked against other fields.'}
                        </p>
                    )}
                </div>
            )}

            {/* §2j row 3 — Bar. A No on any check never passes silently: it is
                flagged LOUDLY at the top of the bar, visible (not sr-only) and
                also spoken via barFlagMessage above.
                DECISION: this renders the FLAG unconditionally on any No — it does
                not read idea_card_bar_failure_mode (linkedin_post_rules.json). That
                value is read by the backend (idea_card_bar.get_bar_failure_mode, called
                from the inbox surfacing wall in work_inbox.py since 3 Oct 2026,
                WI-TC-POST-STUDIO-LANE-RULES-SELECTION-AND-CONSEQUENCE-2026-10-03):
                under "block" a card failing any check never reaches this component, and
                under "flag", the live value, it arrives and this render flags it. The
                consumer decides what reaches this component, never this render. */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
                {failedBarChecks.length > 0 && (
                    <div
                        className={`rounded-lg border p-3 mb-3 ${DESIGN_INTENTS.negative.bg} ${DESIGN_INTENTS.negative.border}`}
                        role="note"
                        aria-label={`${CARD_LABELS.barFailureFlagPrefix || 'Did not clear the bar:'} ${barFlagDetail}`}
                    >
                        <span className={`text-base font-bold uppercase tracking-wide block mb-1 ${DESIGN_INTENTS.negative.text}`}>
                            {CARD_LABELS.barFailureFlagHeading || 'Flagged'}
                        </span>
                        <p className={`text-base font-semibold leading-relaxed ${DESIGN_INTENTS.negative.text}`}>
                            {CARD_LABELS.barFailureFlagPrefix || 'Did not clear the bar:'}
                        </p>
                        {/* A4 (CEO 2026-07-19): the flag says WHY, in plain words. It used to
                            print the failed check's NAME and stop — "Did not clear the bar:
                            Falsifiable" — while the reason sat further down the card under a
                            bare "No". That gap IS the contradiction the CEO read: the banner
                            named a test without saying what was wrong with it. The screen
                            reader was already being told the reason (see the announcer above),
                            so the sighted reader was getting strictly less. */}
                        <ul className="mt-1 space-y-1">
                            {failedBarChecks.map((check) => (
                                <li key={check.label} className={`text-base leading-relaxed ${DESIGN_INTENTS.negative.text}`}>
                                    <span className="font-semibold">{check.label}</span>
                                    {check.reason ? (
                                        <> — {check.reason}</>
                                    ) : (
                                        /* No reason stored. Say that plainly rather than
                                           leaving a bare test name, which reads as though the
                                           card simply declined to explain itself. */
                                        <> — {CARD_LABELS.barFailureNoReason || 'no reason was recorded for this one'}</>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <span className="text-base font-bold text-slate-800 block mb-2">{CARD_LABELS.ideaGateHeading}</span>
                <IdeaGateLine label={CARD_LABELS.ideaGateNonObvious} value={gateReasoning.non_obvious} reason={barReasonNonObvious} />
                <IdeaGateLine label={CARD_LABELS.ideaGateGrounded} value={gateReasoning.groundable_anchor} reason={barReasonGrounded} />
                <IdeaGateLine label={CARD_LABELS.ideaGateFalsifiable} value={gateReasoning.falsifiable} reason={barReasonFalsifiable} />
                {consequenceCheck && (
                    <IdeaGateLine label={CARD_LABELS.ideaGateConsequence || 'Consequence (advisory)'} value={consequenceValue} reason={consequenceReason} />
                )}
            </div>

            {/* §2j row 4 — Trigger block: assembled, never generated (§2i). */}
            {sourceTitle && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <span className="text-base font-bold uppercase tracking-wide block mb-1 text-slate-500">
                        {CARD_LABELS.triggerBlockHeading || 'What triggered this'}
                    </span>
                    {sourceUrl ? (
                        <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-base font-semibold text-brand-700 hover:text-brand-900 transition-colors inline-flex items-center"
                            style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        >
                            {sourceTitle}
                        </a>
                    ) : (
                        <p className="text-base font-semibold text-slate-800">{sourceTitle}</p>
                    )}
                    <p className="text-base text-slate-500 mt-1">
                        {[
                            triggerBlock?.source,
                            triggerBlock?.date_status === 'present' && triggerBlock?.date_published
                                ? triggerBlock.date_published
                                : (CARD_LABELS.dateNotCapturedLabel || 'Publication date not captured'),
                        ].filter(Boolean).join(' — ')}
                    </p>
                    {triggerBlock?.recap && (
                        <p className="text-base leading-relaxed text-slate-700 mt-2">{triggerBlock.recap}</p>
                    )}
                    {whyNow && <p className="text-base text-slate-600 mt-2 italic">{whyNow}</p>}
                </div>
            )}

            {/* §2j row 5 — Receipt line: per-category read counts, never a bare
                checkmark over an empty fetch (§2d). */}
            {receiptLine && (
                <p className="text-base text-slate-500">
                    <span className="font-bold">{CARD_LABELS.receiptHeading || 'What we read'}:</span> {receiptLine}
                </p>
            )}

            {/* F1 — the firm's lane and timing rules applied to this card. Shown whenever the
                backend sent a reading, INCLUDING when every value is "unknown": an unwritten
                rule is a fact the CEO should see, not a line to hide. */}
            {laneTimingLine && (
                <p className="text-base text-slate-500" role="note" aria-label={laneTimingLine}>
                    <span className="font-bold">{CARD_LABELS.laneTimingHeading || 'Lane and timing'}:</span> {laneTimingLine}
                </p>
            )}

            {/* A6 — which tags the View lookup actually searched. Rendered whenever the
                lookup ran, found or not: "no ratified View carries these tags" is only a
                trustworthy statement if the card can say WHICH tags it looked under. A
                silent absence is indistinguishable from a lookup that never happened —
                which is exactly what it was until this leg. Never fabricates: with no tags
                on the article it says so plainly rather than implying a search occurred. */}
            {viewLookupLine && (
                <p className="text-base text-slate-500" role="note" aria-label={viewLookupLine}>
                    <span className="font-bold">{CARD_LABELS.viewLookupHeading || 'View check'}:</span> {viewLookupLine}
                </p>
            )}

            {/* §2j row 6 — the produced-without-a-View stamp, DEMOTED from a banner
                to a quiet line (was a full warning box). Still a role="note" so it
                is announced, but no longer visually shouting. Rendered ONLY when
                the backend says so; its absence is never inferred. */}
            {noViewFallback && (
                <p
                    className="text-base text-slate-500 italic"
                    role="note"
                    aria-label={`${noViewStamp || CARD_LABELS.cardNoViewStamp || 'Written without a ratified View'}${fallbackProof ? ` — ${fallbackProof}` : ''}`}
                >
                    {noViewStamp || CARD_LABELS.cardNoViewStamp || 'Written without a ratified View'}
                    {fallbackProof ? ` — ${fallbackProof}` : ''}
                </p>
            )}

            {/* Not part of the ratified §2j numbered order — a review flag ahead of
                the CEO's decision (§4: near_duplicate_of gets a reader). Kept as a
                visible warning, same as before this leg. */}
            {nearDuplicate && (
                <div
                    className={`rounded-xl border p-4 ${DESIGN_INTENTS.warning.bg} ${DESIGN_INTENTS.warning.border}`}
                    role="note"
                    aria-label={CARD_LABELS.cardNearDuplicateWarning || 'Looks like an existing position'}
                >
                    <span className={`text-base font-bold uppercase tracking-wide block mb-1 ${DESIGN_INTENTS.warning.text}`}>
                        {CARD_LABELS.cardNearDuplicateWarning || 'Looks like an existing position — check before deciding.'}
                    </span>
                    {nearDuplicate.title && (
                        <p className={`text-base ${DESIGN_INTENTS.warning.text}`}>{nearDuplicate.title}</p>
                    )}
                </div>
            )}
        </>
    );
}

// onReject/onRefresh are the Work Inbox surface's own decision plumbing — see
// PreDraftIdeaCard below, the only consumer that needs them. IdeaCardBody above
// takes no such props: it is read-only, so a second consumer (IdeaTabPanel) can
// mount it under its own, already-existing action bar without inheriting a
// second one.
function PreDraftIdeaCard({ item, onReject, onRefresh }) {
    const payload = item.payload || {};
    const postId = payload.post_id || item.id;
    // WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 (T4) — the server declares which of
    // Reject/Approve/Promote is legal on THIS card; the component decides nothing.
    // Absent entirely (every record predating this contract) falls back to showing
    // all three, unchanged from before this window.
    const availableVerbs = payload.available_verbs;

    // #950 announcer pattern — every action result is announced via an
    // aria-live region, not just a toast (a toast alone is not reliably
    // announced to a screen reader).
    // WI-POST-BUTTON-REGRESSION-HUNT-001 — via useAnnouncer, not a bare setState:
    // a repeated identical outcome is a React no-op and would be spoken once and
    // never again (the Post Studio failure class). See the hook for the full why.
    const [statusMessage, announce] = useAnnouncer();
    const [busy, setBusy] = useState(false);
    const [decision, setDecision] = useState(null); // null | 'approved' | 'rejected' | 'promoted'

    const [rejectTaxonomy, setRejectTaxonomy] = useState({ groups: [], other: null });
    useEffect(() => {
        let cancelled = false;
        fetchRejectTaxonomyOnce()
            .then((data) => {
                if (!cancelled) {
                    setRejectTaxonomy({ groups: data?.groups || [], other: data?.other || null });
                }
            })
            .catch(() => {
                // Leave the empty default — the menus simply show nothing
                // rather than crashing the detail renderer.
            });
        return () => { cancelled = true; };
    }, []);

    // Leg 2 — the seven-code reject vocabulary for THIS pre-draft card. Separate
    // fetch from the draft-stage taxonomy above; the Reject button reads this one.
    const [ideaCardRejectCodes, setIdeaCardRejectCodes] = useState([]);
    useEffect(() => {
        let cancelled = false;
        fetchIdeaCardRejectTaxonomyOnce()
            .then((data) => {
                if (!cancelled) setIdeaCardRejectCodes(data?.codes || []);
            })
            .catch(() => {
                // Empty default — the Reject menu shows nothing rather than crashing.
            });
        return () => { cancelled = true; };
    }, []);

    const handleApprove = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            await decideIdeaCard(postId, 'approve');
            setDecision('approved');
            announce(CARD_LABELS.announceCardApproved || 'Idea approved.');
            toast.success(CARD_LABELS.cardApproveSuccess || 'Idea approved.');
            onRefresh?.();
        } catch (err) {
            const msg = err?.message || CARD_LABELS.cardApproveError || 'Could not approve this idea — please try again.';
            announce(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }, [busy, postId, announce, onRefresh]);

    const handlePromote = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            await decideIdeaCard(postId, 'promote');
            setDecision('promoted');
            announce(CARD_LABELS.announceCardPromoted || 'Idea promoted to drafting — reason recorded.');
            toast.success(CARD_LABELS.cardPromoteSuccess || 'Idea promoted to drafting.');
            onRefresh?.();
        } catch (err) {
            const msg = err?.message || CARD_LABELS.cardPromoteError || 'Could not promote this idea — please try again.';
            announce(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }, [busy, postId, announce, onRefresh]);

    const handleReject = useCallback(async (reasonId, attributionId) => {
        if (!onReject || busy) return;
        setBusy(true);
        try {
            await onReject(item.id, reasonId, item.work_type, null, attributionId || null);
            setDecision('rejected');
            announce(CARD_LABELS.announceCardRejected || 'Idea rejected — reason recorded.');
        } catch (err) {
            const msg = err?.message || 'Could not reject this idea — please try again.';
            announce(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }, [onReject, busy, item.id, item.work_type]);

    if (decision) {
        const doneCopy = {
            approved: CARD_LABELS.announceCardApproved || 'Idea approved.',
            promoted: CARD_LABELS.announceCardPromoted || 'Idea promoted to drafting — reason recorded.',
            rejected: CARD_LABELS.announceCardRejected || 'Idea rejected — reason recorded.',
        }[decision];
        return (
            <div className="space-y-4">
                <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>
                <div className={`rounded-xl border p-4 ${DESIGN_INTENTS.positive.bg} ${DESIGN_INTENTS.positive.border}`}>
                    <p className={`text-base font-semibold ${DESIGN_INTENTS.positive.text}`}>{doneCopy}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>

            {/* The ratified numbered order — same function IdeaTabPanel mounts below
                its own action bar. No inline body here any more: this is the split
                the wiring map calls for, not a rewrite of what it renders. */}
            <IdeaCardBody item={item} />

            {/* §2j row 7 — Buttons — Reject (danger, left) | Approve (neutral) | spacer | Promote to Draft (primary, right).
                WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 (T4): a card with an explicit, EMPTY
                available_verbs list gets the loud failure state instead of any button — the
                server is saying nothing is currently legal here, not that the card forgot
                to say so. */}
            <div className="pt-4 border-t border-slate-200">
                {verbsAreKnownAndEmpty(availableVerbs) ? (
                    <CardActionFailure cardId={postId} />
                ) : (
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Leg 2 — the seven-code reject menu. Attribution is derived
                        backend-side (always message_wrong at the card, nothing for
                        card-broken), so the CEO picks a reason, not an attribution. */}
                    {hasVerb(availableVerbs, 'reject') && (
                    <IdeaCardRejectMenu
                        codes={ideaCardRejectCodes}
                        disabled={busy}
                        onPick={(codeId) => handleReject(codeId, null)}
                    />
                    )}

                    {hasVerb(availableVerbs, 'approve') && (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        className={`gap-1.5 text-base font-semibold disabled:opacity-60 ${INTENT_TO_BUTTON.neutral}`}
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        onClick={handleApprove}
                    >
                        {busy ? (
                            <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Working...</>
                        ) : (
                            <><Check weight="bold" className="w-4 h-4" /> Approve</>
                        )}
                    </Button>
                    )}

                    <div className="flex-1" />

{/* Promote asks for NO reason (CEO D-2026-07-20-6). It used to open the
                        DRAFT-stage reject menu, which is how the lone idea_weak-on-a-promote
                        row was created: the CEO advanced an idea and the only vocabulary
                        offered was reject codes, so one had to be picked. Leg 1 of this WI
                        annotated that row as an artifact; this removes the cause. Promote
                        itself is the signal — a plain button, one tap. */}
                    {hasVerb(availableVerbs, 'promote') && (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        className={`gap-1.5 text-base font-semibold disabled:opacity-60 ${INTENT_TO_BUTTON.primary}`}
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        onClick={() => handlePromote()}
                    >
                        {busy ? (
                            <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Working...</>
                        ) : (
                            <><Plus weight="bold" className="w-4 h-4" /> {CARD_LABELS.cardPromoteAction}</>
                        )}
                    </Button>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}

// ── Honest failure receipt — WI-LINKEDIN-IDEA-SYSTEM-END-TO-END-FIX-001 Leg 2b ──
// When an APPROVED idea card's draft dies terminally, the backend stamps a
// failure_receipt on the (gate_rejected) record and surfaces it on the SAME Work
// Inbox as an acknowledge-only WorkItem — title "An approved idea's draft didn't
// work out", actions=["acknowledge"], payload carrying `failure_receipt` +
// `what_you_approved`/`reason`/`next_step`/`outcome`/`source_article_title`, and
// crucially NO `post_text` (backend/routes/work_inbox.py:1750-1803,
// backend/core/agents/linkedin_post/honest_receipt.py). This is its own render —
// plain-English outcome, never the draft editor — so an absent post_text can never
// be mistaken for an empty "Post Draft" (docs/findings/
// LINKEDIN_IDEA_SYSTEM_END_TO_END_ROOTCAUSE_2026-07-24.md).
function FailureReceiptCard({ item, onAccept, onRefresh }) {
    const payload = item.payload || {};
    const receipt = (payload.failure_receipt && typeof payload.failure_receipt === 'object')
        ? payload.failure_receipt
        : {};
    const whatYouApproved = payload.what_you_approved || receipt.what_you_approved || '';
    const reason = payload.reason || receipt.reason_sentence || '';
    const nextStep = payload.next_step || receipt.next_step || '';
    const sourceTitle = payload.source_article_title || receipt.source_article_title || '';
    // Empty string when the words are gone — 90 of the 95 dead drafts predate the
    // ruling that started keeping them, and an empty box would imply we lost them here.
    const failedDraftText = payload.failed_draft_text || '';
    // WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 (T4) — same convention as PreDraftIdeaCard:
    // the id the /api/work/inbox/{id}/... verb routes key on, and the server's closed
    // verb list. Absent entirely falls back to the pre-existing acknowledge-only set.
    const cardItemId = payload.post_id || item.id;
    const availableVerbs = payload.available_verbs;

    // #950 announcer pattern — same convention as PreDraftIdeaCard above.
    const [statusMessage, announce] = useAnnouncer();
    const [busy, setBusy] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [reEvaluateResult, setReEvaluateResult] = useState(null);
    // What the server said the retry did, in words, kept so it stays on the card after the
    // toast fades. WI-THE-IDEAS-TAB-TELLS-THE-TRUTH-001.
    const [reEvaluateNotice, setReEvaluateNotice] = useState(null);

    const handleAcknowledge = useCallback(async () => {
        if (!onAccept || busy) return;
        setBusy(true);
        try {
            await onAccept(item.id, item.work_type, { action: 'acknowledge' });
            setAcknowledged(true);
            announce('Got it.');
        } catch (err) {
            const msg = err?.message || 'Could not acknowledge this update — please try again.';
            announce(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }, [onAccept, busy, item.id, item.work_type, announce]);

    // RE-EVALUATE — a bare CEO-initiated retry of the gate check on this card. Reflects
    // the returned retry_count and outcome; an unrecognised outcome string is never
    // rendered raw (CARD_LABELS.unknownOutcome), and a failing call announces honestly
    // rather than failing silently.
    const handleReEvaluate = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            const result = await reEvaluateWorkItem(cardItemId, 'manual_retry');
            setReEvaluateResult(result);
            // WI-THE-IDEAS-TAB-TELLS-THE-TRUTH-001. The server's answer is now SPOKEN, in
            // its own words and its own tone. Previously every outcome missed this map and
            // fell through to `unknownOutcome`, which was passed only to `announce()` (an
            // sr-only aria-live region) beside an unconditional GREEN success toast — so a
            // successful recirculation and a dead source article looked identical.
            //
            // A free-text outcome (the recomposer's own `withheld_reason`, or the
            // not_applicable sentence) is already CEO-readable English and is shown
            // verbatim rather than translated. Only tokens are looked up.
            const label = OUTCOME_LABELS[result?.outcome];
            const looksLikeSentence = typeof result?.outcome === 'string' && result.outcome.includes(' ');
            const outcomeCopy = label?.text
                || (looksLikeSentence ? result.outcome : null)
                || CARD_LABELS.unknownOutcome
                || 'We got a result we don’t recognise yet — nothing was lost, but please check back on this card.';
            const intent = label?.intent || (looksLikeSentence ? 'warning' : 'warning');
            setReEvaluateNotice({ intent, text: outcomeCopy });
            announce(outcomeCopy);
            // The TONE follows the outcome. A refusal announced as a success is the defect
            // this window exists to end, so only a positive intent gets a success toast.
            if (intent === 'positive') {
                toast.success(outcomeCopy);
            } else {
                toast(outcomeCopy);
            }
            onRefresh?.();
        } catch (err) {
            const msg = err?.message || CARD_LABELS.reEvaluateError || 'Could not check again — please try again.';
            announce(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }, [busy, cardItemId, announce, onRefresh]);

    if (acknowledged) {
        return (
            <div className="space-y-4">
                <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>
                <div className={`rounded-xl border p-4 ${DESIGN_INTENTS.positive.bg} ${DESIGN_INTENTS.positive.border}`}>
                    <p className={`text-base font-semibold ${DESIGN_INTENTS.positive.text}`}>Got it.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div role="status" aria-live="polite" className="sr-only">{statusMessage}</div>

            <div className={`rounded-xl border p-4 ${DESIGN_INTENTS.warning.bg} ${DESIGN_INTENTS.warning.border}`}>
                <span className={`text-sm font-bold uppercase tracking-wide block mb-1 ${DESIGN_INTENTS.warning.text}`}>
                    Update
                </span>
                <p className={`text-lg font-bold leading-relaxed ${DESIGN_INTENTS.warning.text}`}>
                    This idea's draft didn't work out
                </p>
            </div>

            {whatYouApproved && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <span className="text-sm font-bold uppercase tracking-wide block mb-1 text-slate-500">
                        What you approved
                    </span>
                    <p className="text-base leading-relaxed text-slate-700">{whatYouApproved}</p>
                </div>
            )}

            {reason && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <span className="text-sm font-bold uppercase tracking-wide block mb-1 text-slate-500">
                        What happened
                    </span>
                    <p className="text-base leading-relaxed text-slate-700">{reason}</p>
                </div>
            )}

            {/* THE DRAFT ITSELF, READ-ONLY (WI-THE-MAP-IS-THE-TRUTH-IDEA-LANE-001 Task 4).

                A killed draft's record sits at `gate_rejected`, which no tab shows. This
                receipt is its only surface, and it was telling him a draft "didn't work
                out" without ever showing him the draft — while the full text sat on the
                record the whole time.

                Read-only on purpose. The note above this component is right that this card
                must never become the draft editor; showing the words is not editing them,
                and the field is named `failed_draft_text` so it cannot be confused with the
                editor's `post_text`. */}
            {failedDraftText && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <span className="text-sm font-bold uppercase tracking-wide block mb-2 text-slate-500">
                        What was written
                    </span>
                    <p className="text-base leading-relaxed text-slate-700 whitespace-pre-wrap">
                        {failedDraftText}
                    </p>
                </div>
            )}

            {/* WI-ONE-POST-REACHES-THE-CEO-001 Leg 4 — WHICH check, and the sentence.
                This receipt answers "why did my approved idea not become a draft?", and
                until now it answered it only in summary. The annotation is the specific
                half of that same answer, so it belongs directly under "What happened"
                and above "What's next". WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 (T2/T4)
                threads itemId/availableVerbs through so Repair/Overrule can render here
                too, when the server offers them on a failure receipt. */}
            <GateAnnotationNotice
                annotations={payload.gate_annotations}
                itemId={cardItemId}
                availableVerbs={Array.isArray(availableVerbs) ? availableVerbs : []}
            />

            {nextStep && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <span className="text-sm font-bold uppercase tracking-wide block mb-1 text-slate-500">
                        What's next
                    </span>
                    <p className="text-base leading-relaxed text-slate-700">{nextStep}</p>
                </div>
            )}

            {sourceTitle && (
                <div>
                    <span className="text-sm font-bold text-slate-500 block mb-1">Source Article</span>
                    <p className="text-base text-slate-700">{sourceTitle}</p>
                </div>
            )}

            {/* WI-DEAD-CARDS-GET-A-ROUTE-BACK-001 — WHY THERE IS NO "Try again" ON THIS CARD.
                The server decides whether a route back exists and, when none does, says so in
                one plain sentence. Without this block the sentence was computed, shipped in the
                payload, and never read — leaving "Got it" alone to be interpreted as a shrug,
                which is the exact silence this work item exists to end.

                Deliberately NOT merged with `reason` above: that says what happened to the
                draft, this says why nothing more can be tried. Two different honesty claims. */}
            {payload.no_route_back_reason && (
                <div>
                    <span className="text-sm font-bold text-slate-500 block mb-1">Why there is nothing to try</span>
                    <p className="text-base leading-relaxed text-slate-700">{payload.no_route_back_reason}</p>
                </div>
            )}

            {/* WI-THE-IDEAS-TAB-TELLS-THE-TRUTH-001 — WHAT THE RETRY ACTUALLY DID.
                The CEO pressed "Try again", the card WAS recirculated, and the only visible
                feedback was a generic green toast that had already faded by the time he
                looked back. The server's answer now stays ON the card until the card is
                left. Same rounded-border + DESIGN_INTENTS idiom as CardActionFailure and
                the acknowledged banner above — reused, not a fourth invention. */}
            {reEvaluateNotice && (
                <div
                    role="status"
                    aria-live="polite"
                    data-testid="re-evaluate-outcome"
                    data-intent={reEvaluateNotice.intent}
                    className={`rounded-xl border p-4 ${DESIGN_INTENTS[reEvaluateNotice.intent].bg} ${DESIGN_INTENTS[reEvaluateNotice.intent].border}`}
                >
                    <span className={`text-base font-bold uppercase tracking-wide block mb-1 ${DESIGN_INTENTS[reEvaluateNotice.intent].text}`}>
                        What happened
                    </span>
                    <p className={`text-base leading-relaxed ${DESIGN_INTENTS[reEvaluateNotice.intent].text}`}>
                        {reEvaluateNotice.text}
                    </p>
                </div>
            )}

            {/* WI-APPROVED-IDEA-BECOMES-A-DRAFT-003 (T4) — "Got it" is legal only where
                the server says so; an explicit empty available_verbs list is a loud
                render failure, never a silently inert card. */}
            <div className="pt-4 border-t border-slate-200">
                {verbsAreKnownAndEmpty(availableVerbs) ? (
                    <CardActionFailure cardId={cardItemId} />
                ) : (
                <div className="flex items-center gap-3">
                    {reEvaluateResult && (
                        <p className="text-base text-slate-600">
                            {CARD_LABELS.reEvaluateRetryCount && Number.isFinite(reEvaluateResult.retry_count)
                                ? CARD_LABELS.reEvaluateRetryCount(reEvaluateResult.retry_count)
                                : null}
                        </p>
                    )}
                    <div className="flex-1" />
                    {hasVerb(availableVerbs, 're_evaluate') && (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        className={`gap-1.5 text-base font-semibold disabled:opacity-60 ${INTENT_TO_BUTTON.neutral}`}
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        onClick={handleReEvaluate}
                    >
                        {busy ? (
                            <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Working...</>
                        ) : (
                            <><ArrowCounterClockwise weight="bold" className="w-4 h-4" /> {VERB_LABELS.re_evaluate || 'Try again'}</>
                        )}
                    </Button>
                    )}
                    {hasVerb(availableVerbs, 'acknowledge') && (
                    <Button
                        size="sm"
                        disabled={busy}
                        className={`gap-1.5 text-base font-semibold shadow-sm disabled:opacity-60 ${INTENT_TO_BUTTON.primary}`}
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        onClick={handleAcknowledge}
                    >
                        {busy ? (
                            <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Working...</>
                        ) : (
                            <><Check weight="bold" className="w-4 h-4" /> {VERB_LABELS.acknowledge || 'Got it'}</>
                        )}
                    </Button>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}

export function LinkedInPostDetail({ item, onAccept, onReject, onRefresh }) {
    const payload = item.payload || {};

    // WI-IDEA-CARD-BUILD-001 Leg 2: a pre-draft idea card gets its own render —
    // separate action set (approve/reject/promote), no regeneration controls,
    // no post text (none exists yet pre-drafting). Falls through to the
    // existing draft/pending renderer below for every other record.
    if (payload.is_pre_draft_card) {
        return <PreDraftIdeaCard item={item} onReject={onReject} onRefresh={onRefresh} />;
    }

    // WI-LINKEDIN-IDEA-SYSTEM-END-TO-END-FIX-001 Leg 2b — a failure-receipt
    // WorkItem carries payload.failure_receipt (an object) and never post_text.
    // Detected BEFORE any of the draft-render hooks below run (same early-return
    // convention as is_pre_draft_card above) so it never falls into the "Post
    // Draft" editor and renders an empty "0w" draft — the exact defect this leg
    // fixes. The recommendation/actions check is a fallback for a shape that
    // carries the receipt object under a different key in the future.
    const isFailureReceipt = Boolean(payload.failure_receipt && typeof payload.failure_receipt === 'object')
        || (payload.recommendation === 'acknowledge' && Array.isArray(item.actions) && item.actions.includes('acknowledge'));
    if (isFailureReceipt) {
        return <FailureReceiptCard item={item} onAccept={onAccept} onRefresh={onRefresh} />;
    }

    // WI-GATEENVELOPE-SILENT-200-AUDIT-AND-FIX-001 (L-055): these actions run
    // through the inbox accept/reject endpoints, which can now return a real
    // non-200. A toast alone is not reliably announced (#950), so a failure is
    // spoken here too — the CEO reads by screen reader.
    const [actionStatus, announceAction] = useAnnouncer();

    const postType = payload.post_type || item.work_type || '';
    const postId = payload.post_id || payload._id || item.id;
    const isDraft = (payload.status || item.status || '').toLowerCase() === 'draft';

    // Mutable post state — updated on regeneration
    const [currentText, setCurrentText] = useState(payload.post_text || payload.post_text_original || '');
    const [originalText] = useState(payload.post_text_original || payload.post_text || '');
    const firstCommentText = payload.first_comment_text || payload.optimizer_output?.first_comment_text || '';
    // WI-LINKEDIN-IDEA-GATE-OBSERVE-BUILD-001: one plain-English sentence — the
    // founder/investor takeaway this post is trying to land. Absent on legacy rows.
    const intendedMessage = payload.intended_message || '';
    const blogUrl = payload.blog_url || '';
    const signalStrength = payload.signal_strength;
    const sourceTitle = payload.source_article_title || '';
    const sourceUrl = payload.source_article_url || '';
    const masterDocTitle = payload.master_doc_title || '';
    const associateBriefs = payload.associate_briefs || [];
    const [calibration, setCalibration] = useState('agent_got_it_right');
    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(currentText);
    const [briefsOpen, setBriefsOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    // Regeneration state
    const [modifiers, setModifiers] = useState(() => {
        const initial = {};
        REGEN_MODIFIERS.forEach(m => { initial[m.key] = REGEN_STATES.neutral; });
        return initial;
    });
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenCount, setRegenCount] = useState(payload.regeneration_count || 0);
    const [regenOriginalText, setRegenOriginalText] = useState(payload.post_text_original || '');
    const [showOriginal, setShowOriginal] = useState(false);
    const [regenSuccess, setRegenSuccess] = useState(false);

    // WI-LINKEDIN-REJECT-REASONS-AND-LEARNING-LOOP-001: grouped, one-tap reject
    // taxonomy fetched at runtime from GET /api/linkedin-posts/reject-reasons.
    // Picking any reason fires the reject immediately with the group's derived
    // attribution — replaces the old two-step reason-then-attribution flow.
    const [rejectTaxonomy, setRejectTaxonomy] = useState({ groups: [], other: null });

    useEffect(() => {
        let cancelled = false;
        fetchRejectTaxonomyOnce()
            .then((data) => {
                if (!cancelled) {
                    setRejectTaxonomy({
                        groups: data?.groups || [],
                        other: data?.other || null,
                    });
                }
            })
            .catch(() => {
                // Leave the empty default — the reject menu simply shows nothing
                // rather than crashing the detail renderer.
            });
        return () => { cancelled = true; };
    }, []);

    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const textHasChanged = editedText !== currentText;
    const wordCount = useMemo(() => currentText.trim().split(/\s+/).filter(Boolean).length, [currentText]);
    // Defense-in-depth backstop (WI-LINKEDIN-IDEA-SYSTEM-END-TO-END-FIX-001 Leg 2b):
    // the failure-receipt branch above is the primary fix, but a "Post Draft" heading
    // over a blank, EDITABLE textarea reading "0w" must never render as though a real
    // draft exists — for ANY item that reaches this far (is_pre_draft_card and
    // isFailureReceipt have already returned above). Whitespace-only counts as empty.
    const hasDraftBody = currentText.trim().length > 0;

    // Check if any modifier is non-neutral
    const hasActiveModifiers = useMemo(
        () => Object.values(modifiers).some(v => v !== REGEN_STATES.neutral),
        [modifiers]
    );

    const handleModifierChange = useCallback((key, newState) => {
        setModifiers(prev => ({ ...prev, [key]: newState }));
    }, []);

    const handleRegenerate = useCallback(async () => {
        if (!hasActiveModifiers || isRegenerating) return;

        setIsRegenerating(true);
        setRegenSuccess(false);

        try {
            const result = await regenerateLinkedInPost(postId, modifiers);

            // Success — update post text and reset dials
            setCurrentText(result.post_text);
            setEditedText(result.post_text);
            setRegenCount(result.regeneration_count);
            if (result.post_text_original) {
                setRegenOriginalText(result.post_text_original);
            }

            // Reset modifiers to neutral
            setModifiers(prev => {
                const reset = {};
                Object.keys(prev).forEach(k => { reset[k] = REGEN_STATES.neutral; });
                return reset;
            });

            // Flash success
            setRegenSuccess(true);
            setTimeout(() => setRegenSuccess(false), 3000);
        } catch (err) {
            // Spec TC-08/TC-09: branch on err.status (set by lib/api.js request wrapper).
            // err.message holds the backend `detail` so we surface it on 400.
            if (err?.status === 422) {
                toast.error(REGEN_CONFIG.gateFailedMessage || "Draft didn't pass quality review — try different settings");
            } else if (err?.status === 400) {
                toast.error(err.message || REGEN_CONFIG.errorMessage || 'Something went wrong — please try again');
            } else {
                toast.error(REGEN_CONFIG.errorMessage || 'Something went wrong — please try again');
            }
        } finally {
            setIsRegenerating(false);
        }
    }, [hasActiveModifiers, isRegenerating, postId, modifiers]);

    const handleSaveEdit = useCallback(async () => {
        if (!textHasChanged || isSavingEdit) return;
        setIsSavingEdit(true);
        try {
            await saveLinkedInPostEdits(postId, editedText);
            setCurrentText(editedText);
            setIsEditing(false);
            toast.success('Edits saved');
        } catch {
            toast.error('Could not save edits — please try again');
        } finally {
            setIsSavingEdit(false);
        }
    }, [textHasChanged, isSavingEdit, postId, editedText]);

    const handleSaveDraft = useCallback(async () => {
        if (onAccept) {
            setBusy(true);
            try {
                const textToSend = isEditing ? editedText : currentText;
                const payload = textToSend !== originalText
                    ? { action: 'save_draft', edited_text: textToSend, calibration }
                    : { action: 'save_draft', calibration };
                await onAccept(item.id, item.work_type, payload);
            } catch (err) {
                announceAction(err?.message || 'Could not save this draft — please try again.');
            } finally {
                setBusy(false);
            }
        }
    }, [onAccept, item.id, item.work_type, isEditing, editedText, currentText, originalText, calibration, announceAction]);

    const handleMarkPublished = useCallback(async () => {
        if (onAccept) {
            setBusy(true);
            try {
                const textToSend = isEditing ? editedText : currentText;
                const payload = textToSend !== originalText
                    ? { action: 'mark_published', edited_text: textToSend, calibration }
                    : { action: 'mark_published', calibration };
                await onAccept(item.id, item.work_type, payload);
            } catch (err) {
                announceAction(err?.message || 'Could not mark this as published — please try again.');
            } finally {
                setBusy(false);
            }
        }
    }, [onAccept, item.id, item.work_type, isEditing, editedText, currentText, originalText, calibration, announceAction]);

    const handleReject = useCallback(async (reasonId, attributionId) => {
        if (onReject) {
            setBusy(true);
            try {
                // Spec TC-10c: forward calibration to the inbox reject path.
                // WI-LINKEDIN-IDEA-GATE-OBSERVE-BUILD-001: attribution is additive and
                // optional (null when the CEO does not pick one) — never blocks reject.
                await onReject(item.id, reasonId, item.work_type, calibration, attributionId || null);
            } catch (err) {
                announceAction(err?.message || 'Could not reject this post — please try again.');
            } finally {
                setBusy(false);
            }
        }
    }, [onReject, item.id, item.work_type, calibration, announceAction]);

    return (
        <div className="space-y-6">
            <div role="status" aria-live="polite" className="sr-only">{actionStatus}</div>
            {/* Post Type Badge — signal-strength pill removed per WI-LINKEDIN-POST-FRONTEND-V1
                (editorial judgment now lives on the Ranker score; signal_strength still
                flows through learning_signals but is no longer a UI surface). */}
            <div className="flex items-center gap-3 flex-wrap">
                <span className={`inline-flex px-3 py-1.5 rounded-full text-base font-bold border ${SEMANTIC_COLORS.confidence.high.bg} ${SEMANTIC_COLORS.confidence.high.text} ${SEMANTIC_COLORS.confidence.high.border}`}>
                    {POST_TYPE_LABELS[postType] || postType.replace(/_/g, ' ')}
                </span>
            </div>

            {/* Intended Message — the CEO's at-a-glance "what is this post trying to
                say". WI-LINKEDIN-IDEA-GATE-OBSERVE-BUILD-001. Absent on legacy rows —
                render nothing when payload.intended_message is empty/null. */}
            {intendedMessage && (
                <div
                    className={`rounded-xl border p-4 ${DESIGN_INTENTS.info.bg} ${DESIGN_INTENTS.info.border}`}
                    aria-label={`Intended message: ${intendedMessage}`}
                >
                    <span className={`text-sm font-bold uppercase tracking-wide block mb-1 ${DESIGN_INTENTS.info.text}`}>
                        Intended message
                    </span>
                    <p className={`text-base leading-relaxed ${DESIGN_INTENTS.info.text}`}>
                        {intendedMessage}
                    </p>
                </div>
            )}

            {/* Post Text — read-only or editable.
                Defense-in-depth backstop (see hasDraftBody above): when there is no
                draft body, this renders an honest "no draft body" note instead of the
                editor — never a blank, EDITABLE "Post Draft" textarea reading "0w" as
                though a real draft exists. */}
            <div>
                {hasDraftBody ? (
                    <>
                        {/* WI-ONE-POST-REACHES-THE-CEO-001 Leg 4 — the check that fired, on the
                            surface that carries the Approve and Reject buttons. This is the
                            seat the 2026-08-01 ruling handed the decision to; it is the one
                            place the annotation could least afford to be missing. */}
                        <GateAnnotationNotice
                            annotations={payload.gate_annotations}
                            itemId={postId}
                            availableVerbs={Array.isArray(payload.available_verbs) ? payload.available_verbs : []}
                        />
                        <UnresolvedNotes notes={payload.unresolved_notes} />
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                                <span className="text-base font-bold text-slate-800">Post Draft</span>
                                <span className="text-sm font-semibold text-slate-500">
                                    {wordCount}w
                                </span>
                                {regenCount > 0 && (
                                    <span className="text-sm font-semibold text-slate-400">
                                        Regenerated {regenCount}x
                                    </span>
                                )}
                                {regenSuccess && (
                                    <span className={`text-sm font-semibold ${DESIGN_INTENTS.positive.text} transition-opacity`}>
                                        {REGEN_CONFIG.successMessage || 'Draft updated'}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <CopyButton text={currentText} label={SHARED_UI_LABELS.copyPost} />
                                {isEditing ? (
                                    <button
                                        onClick={handleSaveEdit}
                                        disabled={!textHasChanged || isSavingEdit}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${INTENT_TO_BUTTON.neutral.bg} text-sm font-semibold ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} transition-colors disabled:opacity-50`}
                                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                                    >
                                        {isSavingEdit ? (
                                            <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Saving...</>
                                        ) : (
                                            <><Check weight="bold" className="w-4 h-4" /> Save</>
                                        )}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${INTENT_TO_BUTTON.neutral.bg} text-sm font-semibold ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} transition-colors`}
                                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                                    >
                                        <PencilSimple weight="bold" className="w-4 h-4" /> Edit
                                    </button>
                                )}
                            </div>
                        </div>
                        {isEditing ? (
                            <div>
                                <textarea
                                    value={editedText}
                                    onChange={(e) => setEditedText(e.target.value)}
                                    className="w-full bg-white rounded-xl border-2 border-brand-200 p-4 text-lg text-slate-800 whitespace-pre-wrap leading-relaxed focus:outline-none focus:border-brand-500 resize-y"
                                    style={{ minHeight: '200px' }}
                                    aria-label={SHARED_UI_LABELS.editPostText}
                                />
                                <div className="flex items-center gap-2 mt-2">
                                    <button
                                        onClick={() => { setIsEditing(false); setEditedText(currentText); }}
                                        className="text-sm font-semibold text-slate-500 hover:text-slate-700 px-3"
                                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                                    >
                                        Cancel edits
                                    </button>
                                    {textHasChanged && (
                                        <span className="text-sm font-medium text-amber-600">
                                            Post has unsaved edits
                                        </span>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                                <p className="text-lg text-slate-800 whitespace-pre-wrap leading-relaxed">
                                    {currentText}
                                </p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className={`rounded-xl border p-4 ${DESIGN_INTENTS.muted.bg} ${DESIGN_INTENTS.muted.border}`}>
                        <span className="text-base font-bold text-slate-800 block mb-1">Post Draft</span>
                        <p className={`text-base leading-relaxed ${DESIGN_INTENTS.muted.text}`}>
                            No draft text is available for this post yet.
                        </p>
                    </div>
                )}
            </div>

            {/* Calibration feedback pills */}
            {PS_CALIBRATION.length > 0 && (
                <div>
                    <p className="text-sm font-bold text-slate-700 mb-2">How did the agent do?</p>
                    <div className="flex flex-wrap gap-2">
                        {PS_CALIBRATION.map(opt => {
                            const isSelected = calibration === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    onClick={() => setCalibration(opt.id)}
                                    className={`px-3 py-1.5 border text-sm font-semibold transition-colors ${
                                        isSelected
                                            ? ''
                                            : `${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover}`
                                    }`}
                                    style={{
                                        minHeight: DS_SPACING.minTouchTarget + 'px',
                                        borderRadius: DS_BORDERS.radiusPill,
                                        ...(isSelected ? {
                                            borderColor: DS_COLORS.border.brand,
                                            color: DS_COLORS.text.brand,
                                            background: DS_COLORS.surface.warm,
                                        } : {}),
                                    }}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Regeneration Controls — only for drafts */}
            {isDraft && REGEN_MODIFIERS.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <span className="text-base font-bold text-slate-800 block mb-3">
                        Adjust Draft
                    </span>

                    {/* Modifier dials */}
                    <div className="space-y-1">
                        {REGEN_MODIFIERS.map((mod) => {
                            const isDisabled = (mod.disabled_for || []).includes(postType);
                            const dialLabel = mod.key === 'length'
                                ? `${mod.label} (${wordCount}w)`
                                : mod.label;

                            return (
                                <ModifierDial
                                    key={mod.key}
                                    label={dialLabel}
                                    state={modifiers[mod.key] || REGEN_STATES.neutral}
                                    disabled={isDisabled || isRegenerating}
                                    onChange={(newState) => handleModifierChange(mod.key, newState)}
                                />
                            );
                        })}
                    </div>

                    {/* Regenerate button */}
                    <div className="mt-4">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={!hasActiveModifiers || isRegenerating || busy}
                            className="gap-1.5 text-slate-700 border-slate-200 hover:border-brand-900 hover:text-brand-900 text-base font-semibold disabled:opacity-50"
                            style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                            onClick={handleRegenerate}
                        >
                            {isRegenerating ? (
                                <>
                                    <SpinnerGap weight="bold" className="w-4 h-4 animate-spin" />
                                    {REGEN_CONFIG.loadingLabel || 'Refining your draft...'}
                                </>
                            ) : (
                                <>
                                    <ArrowCounterClockwise weight="bold" className="w-4 h-4" />
                                    {REGEN_CONFIG.buttonLabel || 'Regenerate Draft'}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {/* Original Draft — shown after regeneration */}
            {regenCount > 0 && regenOriginalText && regenOriginalText !== currentText && (
                <div>
                    <button
                        onClick={() => setShowOriginal(!showOriginal)}
                        className="text-sm font-semibold text-brand-700 hover:text-brand-900 transition-colors px-1"
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                    >
                        {showOriginal ? 'Hide original draft' : 'View original draft'}
                    </button>
                    {showOriginal && (
                        <div className={`mt-2 rounded-xl border p-4 ${DESIGN_INTENTS.muted.bg} ${DESIGN_INTENTS.muted.border}`}>
                            <p className={`text-base leading-relaxed whitespace-pre-wrap ${DESIGN_INTENTS.muted.text}`}>
                                {regenOriginalText}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* First Comment Text (if blog link exists) */}
            {firstCommentText && (
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-base font-bold text-slate-800">First Comment (posted separately on LinkedIn)</span>
                        <CopyButton text={firstCommentText} label={SHARED_UI_LABELS.copyComment} />
                    </div>
                    <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
                        <p className="text-base text-slate-700">{firstCommentText}</p>
                    </div>
                </div>
            )}

            {/* Blog Link */}
            {blogUrl && (
                <div>
                    <a
                        href={blogUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-base font-semibold text-brand-700 hover:text-brand-900 transition-colors"
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                    >
                        <ArrowSquareOut weight="bold" className="w-4 h-4" />
                        Read Accrezeo Blog
                    </a>
                </div>
            )}

            {/* Source Article */}
            {sourceTitle && (
                <div>
                    <span className="text-sm font-bold text-slate-500 block mb-1">Source Article</span>
                    {sourceUrl ? (
                        <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-base font-semibold text-brand-700 hover:text-brand-900 transition-colors inline-flex items-center"
                            style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        >
                            {sourceTitle}
                        </a>
                    ) : (
                        <p className="text-base text-slate-700">{sourceTitle}</p>
                    )}
                </div>
            )}

            {/* Connected Master Doc (Type 1 only) */}
            {masterDocTitle && (
                <div>
                    <span className="text-sm font-bold text-slate-500 block mb-1">Connected Master Doc</span>
                    <p className="text-base text-slate-700">{masterDocTitle}</p>
                </div>
            )}

            {/* Associate Briefs — collapsible accordion */}
            {associateBriefs.length > 0 && (
                <div>
                    <button
                        onClick={() => setBriefsOpen(!briefsOpen)}
                        className="w-full flex items-center justify-between py-2"
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        aria-expanded={briefsOpen}
                    >
                        <span className="text-base font-bold text-slate-800">
                            Agent Thinking
                        </span>
                        {briefsOpen ? (
                            <CaretUp weight="bold" className="w-4 h-4 text-slate-400" />
                        ) : (
                            <CaretDown weight="bold" className="w-4 h-4 text-slate-400" />
                        )}
                    </button>
                    {briefsOpen && (
                        <div className="space-y-3 mt-2">
                            {associateBriefs.filter(Boolean).map((brief, i) => {
                                const lens = brief.lens || `associate_${i + 1}`;
                                const output = brief.output || '';
                                return (
                                    <div key={lens} className="bg-slate-50 rounded-lg border border-slate-100 p-4">
                                        <span className="text-sm font-bold text-slate-600 block mb-2">
                                            {ASSOCIATE_LABELS[lens] || lens.replace(/_/g, ' ')}
                                        </span>
                                        <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                                            {output}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Actions — Reject (danger, left) | Save Draft (neutral, middle) | spacer | I posted it by hand (primary, right) */}
            <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center gap-3">
                    {/* Reject (dropdown) — danger, left. WI-LINKEDIN-REJECT-REASONS-
                        AND-LEARNING-LOOP-001: grouped, one-tap reject menu — picking
                        any reason fires the reject immediately with the group's
                        derived attribution (Other -> null attribution). */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={busy || isRegenerating}
                                className={`gap-1.5 text-base font-semibold disabled:opacity-60 ${INTENT_TO_BUTTON.negative}`}
                                style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                            >
                                <X weight="bold" className="w-4 h-4" />
                                Reject
                                <CaretDown weight="bold" className="w-3 h-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="z-[100] bg-white border border-slate-200 shadow-lg rounded-md w-72 max-h-96 overflow-y-auto">
                            {rejectTaxonomy.groups.map((group, idx) => (
                                <React.Fragment key={group.attribution}>
                                    {idx > 0 && <DropdownMenuSeparator />}
                                    <DropdownMenuLabel className="text-sm font-bold uppercase tracking-wide text-slate-500">
                                        {group.group_label}
                                    </DropdownMenuLabel>
                                    {(group.reasons || []).map((reason) => (
                                        <DropdownMenuItem
                                            key={reason.id}
                                            className="text-base py-3 cursor-pointer font-semibold text-slate-800"
                                            onSelect={() => handleReject(reason.id, group.attribution)}
                                        >
                                            {reason.label}
                                        </DropdownMenuItem>
                                    ))}
                                </React.Fragment>
                            ))}
                            {rejectTaxonomy.other && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="text-base py-3 cursor-pointer font-semibold text-slate-800"
                                        onSelect={() => handleReject(rejectTaxonomy.other.id, null)}
                                    >
                                        {rejectTaxonomy.other.label}
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Save Draft — neutral, middle */}
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={busy || isRegenerating}
                        className={`gap-1.5 text-base font-semibold shadow-sm disabled:opacity-60 ${INTENT_TO_BUTTON.neutral}`}
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        onClick={handleSaveDraft}
                    >
                        {busy ? (
                            <><SpinnerGap weight="bold" className="w-4 h-4 animate-spin" /> Saving...</>
                        ) : (
                            <><Check weight="bold" className="w-4 h-4" /> Save Draft</>
                        )}
                    </Button>

                    <div className="flex-1" />

                    {/* I posted it by hand — a declaration, not a delivery receipt (R25) */}
                    <Button
                        size="sm"
                        disabled={busy || isRegenerating}
                        className={`gap-1.5 text-base font-semibold shadow-sm disabled:opacity-60 ${INTENT_TO_BUTTON.primary}`}
                        style={{ minHeight: DS_SPACING.minTouchTarget + 'px' }}
                        onClick={handleMarkPublished}
                    >
                        <Check weight="bold" className="w-4 h-4" /> I posted it by hand
                    </Button>
                </div>
            </div>
        </div>
    );
}
